import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Tag,
  message,
  Popconfirm,
  Switch,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as fileService from '../services/fileService';

const { TextArea } = Input;

const FileConfig: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState<fileService.FileConfig[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchName, setSearchName] = useState('');
  const [searchType, setSearchType] = useState('');

  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<fileService.FileConfig | null>(null);
  const [form] = Form.useForm();
  const [storageType, setStorageType] = useState('local');

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const res = await fileService.listConfigs({
        page,
        pageSize,
        name: searchName || undefined,
        storageType: searchType || undefined,
      });
      setConfigs(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    loadConfigs();
  };

  const handleReset = () => {
    setSearchName('');
    setSearchType('');
    setPage(1);
    loadConfigs();
  };

  const handleAdd = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({ status: 1, isDefault: 0, maxSize: 10485760, storageType: 'local' });
    setStorageType('local');
    setModalVisible(true);
  };

  const handleEdit = (record: fileService.FileConfig) => {
    setEditingConfig(record);
    form.setFieldsValue(record);
    setStorageType(record.storageType);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await fileService.deleteConfig(id);
      message.success('删除成功');
      loadConfigs();
    } catch (err: any) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingConfig) {
        await fileService.updateConfig(editingConfig.id, values);
        message.success('更新成功');
      } else {
        await fileService.createConfig(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadConfigs();
    } catch {
      // 表单验证失败
    }
  };

  const handleStorageTypeChange = (value: string) => {
    setStorageType(value);
  };

  const columns: ColumnsType<fileService.FileConfig> = [
    { title: '配置名称', dataIndex: 'name', key: 'name', width: 120 },
    { title: '配置编码', dataIndex: 'code', key: 'code', width: 100 },
    {
      title: '存储类型',
      dataIndex: 'storageType',
      key: 'storageType',
      width: 100,
      render: (type) => {
        const opt = fileService.storageTypeOptions.find((o) => o.value === type);
        return opt?.label || type;
      },
    },
    {
      title: '最大文件大小',
      dataIndex: 'maxSize',
      key: 'maxSize',
      width: 100,
      render: (size) => fileService.formatFileSize(size),
    },
    {
      title: '默认',
      dataIndex: 'isDefault',
      key: 'isDefault',
      width: 60,
      render: (val) => (val === 1 ? <Tag color="blue">是</Tag> : '-'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size={0} separator={<span style={{ color: '#d9d9d9' }}>|</span>}>
          <Button type="link" size="small" style={{ padding: '0 4px' }} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          {record.code !== 'local' ? (
            <Popconfirm title="确定删除此配置？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" size="small" danger style={{ padding: '0 4px' }}>
                删除
              </Button>
            </Popconfirm>
          ) : (
            <Button type="link" size="small" disabled style={{ padding: '0 4px' }}>
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // 根据存储类型渲染不同的配置字段
  const renderStorageFields = () => {
    switch (storageType) {
      case 'local':
        return (
          <>
            <Form.Item name="basePath" label="存储路径" rules={[{ required: true, message: '请输入存储路径' }]}>
              <Input placeholder="如: ./uploads" />
            </Form.Item>
            <Form.Item name="baseUrl" label="访问URL" rules={[{ required: true, message: '请输入访问URL' }]}>
              <Input placeholder="如: http://localhost:8080/uploads" />
            </Form.Item>
          </>
        );
      case 'database':
        return (
          <Form.Item name="remark" label="说明">
            <TextArea rows={2} placeholder="文件将存储在数据库中" />
          </Form.Item>
        );
      case 'ftp':
      case 'sftp':
        return (
          <>
            <Form.Item name="endpoint" label="服务器地址" rules={[{ required: true, message: '请输入服务器地址' }]}>
              <Input placeholder="如: ftp.example.com" />
            </Form.Item>
            <Form.Item name="port" label="端口">
              <InputNumber style={{ width: '100%' }} placeholder={storageType === 'ftp' ? '21' : '22'} />
            </Form.Item>
            <Form.Item name="accessKey" label="用户名">
              <Input placeholder="FTP用户名" />
            </Form.Item>
            <Form.Item name="secretKey" label="密码">
              <Input.Password placeholder="FTP密码" />
            </Form.Item>
            <Form.Item name="basePath" label="远程路径">
              <Input placeholder="如: /uploads" />
            </Form.Item>
            <Form.Item name="baseUrl" label="访问URL">
              <Input placeholder="文件访问基础URL" />
            </Form.Item>
          </>
        );
      case 's3':
        return (
          <>
            <Form.Item name="endpoint" label="节点地址" rules={[{ required: true, message: '请输入节点地址' }]}>
              <Input placeholder="请输入节点地址" />
            </Form.Item>
            <Form.Item name="bucket" label="存储桶" rules={[{ required: true, message: '请输入存储桶名称' }]}>
              <Input placeholder="请输入Bucket名称" />
            </Form.Item>
            <Form.Item name="region" label="区域">
              <Input placeholder="请输入区域" />
            </Form.Item>
            <Form.Item name="accessKey" label="AccessKey" rules={[{ required: true, message: '请输入AccessKey' }]}>
              <Input placeholder="请输入AccessKey" />
            </Form.Item>
            <Form.Item name="secretKey" label="SecretKey">
              <Input.Password placeholder="请输入SecretKey" />
            </Form.Item>
            <Form.Item name="basePath" label="路径前缀">
              <Input placeholder="如: uploads/" />
            </Form.Item>
            <Form.Item name="baseUrl" label="自定义域名">
              <Input placeholder="请输入自定义域名" />
            </Form.Item>
          </>
        );
      case 'qiniu':
        return (
          <>
            <Form.Item name="bucket" label="空间名称" rules={[{ required: true, message: '请输入空间名称' }]}>
              <Input placeholder="请输入Bucket(空间名称)" />
            </Form.Item>
            <Form.Item name="accessKey" label="AccessKey" rules={[{ required: true, message: '请输入AccessKey' }]}>
              <Input placeholder="请输入AccessKey" />
            </Form.Item>
            <Form.Item name="secretKey" label="SecretKey">
              <Input.Password placeholder="请输入SecretKey" />
            </Form.Item>
            <Form.Item name="basePath" label="路径前缀">
              <Input placeholder="如: uploads/" />
            </Form.Item>
            <Form.Item name="baseUrl" label="域名" rules={[{ required: true, message: '请输入域名' }]}>
              <Input placeholder="请输入访问域名，如: https://xxx.com" />
            </Form.Item>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="文件配置"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadConfigs}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增配置
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="配置名称"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            style={{ width: 150 }}
            allowClear
          />
          <Select
            placeholder="存储类型"
            value={searchType || undefined}
            onChange={setSearchType}
            style={{ width: 150 }}
            allowClear
          >
            {fileService.storageTypeOptions.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>

        <Table
          columns={columns}
          dataSource={configs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      <Modal
        title={editingConfig ? '编辑配置' : '新增配置'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        width={600}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="配置名称" rules={[{ required: true, message: '请输入配置名称' }]}>
            <Input placeholder="请输入配置名称" />
          </Form.Item>
          <Form.Item name="code" label="配置编码" rules={[{ required: true, message: '请输入配置编码' }]}>
            <Input placeholder="唯一标识，如: local, aliyun-oss" disabled={!!editingConfig} />
          </Form.Item>
          <Form.Item name="storageType" label="存储类型" rules={[{ required: true, message: '请选择存储类型' }]}>
            <Select onChange={handleStorageTypeChange} disabled={editingConfig?.code === 'local'}>
              {fileService.storageTypeOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {renderStorageFields()}

          <Form.Item name="maxSize" label="最大文件大小(字节)">
            <InputNumber style={{ width: '100%' }} min={1} placeholder="默认10MB" />
          </Form.Item>
          <Form.Item name="allowedTypes" label="允许的文件类型">
            <Input placeholder="如: jpg,png,pdf,doc，留空表示不限制" />
          </Form.Item>
          <Form.Item name="isDefault" label="设为默认" valuePropName="checked" getValueFromEvent={(checked) => checked ? 1 : 0} getValueProps={(value) => ({ checked: value === 1 })}>
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select>
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FileConfig;
